import React, { useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import Input from '@mui/material/Input';
import Button from '@mui/material/Button';
import { Check, X, Edit2 } from 'lucide-react';
import Calendar from '@mui/icons-material/CalendarToday';
import Clock from '@mui/icons-material/Schedule';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEditorPublish, updateProjectName } from '@modules/editor';
import api from '@utils/axiosInstance';

interface EditorHeaderProps {
    isLoading: boolean;
}

function EditorHeader({ isLoading }: EditorHeaderProps) {
    const { edit_id } = useParams();
    const projectInfo = useSelector((state: any) => state.editor.projectInfo);
    const { isPublishing, isUpdatePendingRedux, handlePublish } = useEditorPublish();
    const { uuid } = useParams();
    const editProjIdx = useSelector((state: any) => state.editor.editProjIdx);
    const dispatch = useDispatch();
    const userToken = useSelector((state: any) => state.organization.userToken);
    const organizationData = useSelector((state: any) => state.organization.organizationData);
    const { t } = useTranslation();

    const [projectName, setProjectName] = useState(projectInfo[0]?.label || "New Project")
    const [isEditing, setIsEditing] = useState(false)
    const [tempName, setTempName] = useState(projectInfo[0]?.label || "New Project")
    const [isUpdatingName, setIsUpdatingName] = useState(false)

    const getEditProjectId = () => {
        if (typeof editProjIdx === 'number' && editProjIdx > -1) {
            return String(editProjIdx);
        } else {
            const currentUrl = window.location.href;
            const urlParts = currentUrl.split('/');
            return urlParts[urlParts.length - 1];
        }
    };


    const handleEditClick = () => {
        setIsEditing(true)
        setTempName(projectName)
    }

    const handleSave = async () => {
        if (tempName.trim() === '') {
            alert(t('vod.manage.editor_tab.enter_project_name'));
            return;
        }

        setIsUpdatingName(true);
        try {
            // 1. Redux에서 projectInfo 업데이트
            dispatch(updateProjectName(tempName));
            setProjectName(tempName);

            // 2. API 호출하여 서버에 업데이트
            await api({
                method: 'post',
                url: '/editor/update',
                data: {
                    tk: userToken,
                    uuid: uuid,
                    ogc: organizationData.organization_code,
                    project_id: edit_id,
                    projectInfo: {
                        label: tempName
                    }
                }
            });

            setIsEditing(false);
        } catch (error) {
            console.error('프로젝트 이름 업데이트 실패:', error);
            alert(t('vod.manage.editor_tab.project_name_update_failed'));
            // 실패 시 원래 이름으로 되돌리기
            setTempName(projectName);
        } finally {
            setIsUpdatingName(false);
        }
    }

    const handleCancel = () => {
        setTempName(projectName)
        setIsEditing(false)
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSave()
        } else if (e.key === "Escape") {
            handleCancel()
        }
    }

    const onPublish = () => {
        const editProjectId = getEditProjectId();
        if (uuid && editProjectId) {
            handlePublish(uuid, editProjectId);
        }
    };

    if (isLoading) {
        return (
            <div className="editor-header">
                <div className="editor-header__row">
                    <div className="editor-header__left">
                        <div className="editor-header__title-group">
                            <Skeleton variant="text" width={100} height={20} />
                        </div>
                        <div className="editor-header__meta-group">
                            <div className="editor-header__meta">
                                <Skeleton variant="text" width={100} height={20} />
                            </div>
                            <div className="editor-header__meta">
                                <Skeleton variant="text" width={100} height={20} />
                            </div>
                        </div>
                    </div>
                    <div className="editor-header__right">
                        <Skeleton variant="rectangular" width={100} height={20} />
                    </div>
                </div>
            </div>
        );
    }

    if (!edit_id) {
        return null;
    }

    return (
        <div className="editor-header">
            <div className="editor-header__row">
                <div className="editor-header__left">
                    <div className="editor-header__title-group">
                        <div className="editor-header__project-name-container">
                            {isEditing ? (
                                <div className="editor-header__button-group">
                                    <Input
                                        value={tempName}
                                        onChange={(e) => setTempName(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        className="editor-header__project-name-input"
                                        autoFocus
                                    />
                                    <Button
                                        size="small"
                                        onClick={handleSave}
                                        disabled={isUpdatingName}
                                        className="editor-header__save-button"
                                    >
                                        {isUpdatingName ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <Check />
                                        )}
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={handleCancel}
                                        className="editor-header__cancel-button"
                                    >
                                        <X />
                                    </Button>
                                </div>
                            ) : (
                                <div className="editor-header__project-name-wrapper">
                                    <h1 className="editor-header__project-name-display">{projectName}</h1>
                                    <Button
                                        size="small"
                                        onClick={handleEditClick}
                                        className="editor-header__edit-button"
                                    >
                                        <Edit2 />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="editor-header__meta-group">
                        <div className="editor-header__meta">
                            <Calendar className="editor-header__icon" />
                            <span>2024.01.15</span>
                        </div>
                        <div className="editor-header__meta">
                            <Clock className="editor-header__icon" />
                            <span>{t('vod.manage.editor_tab.last_modified')}: 14:30</span>
                        </div>
                    </div>
                </div>
                <div className="editor-header__right">
                    <Button
                        className="editor-header__publish-btn"
                        color="primary"
                        disabled={isPublishing || isUpdatePendingRedux}
                        startIcon={isPublishing || isUpdatePendingRedux ? <CircularProgress size={20} color="inherit" /> : null}
                        onClick={onPublish}
                    >
                        {isPublishing ? t('vod.manage.editor_tab.publishing') : isUpdatePendingRedux ? t('vod.manage.editor_tab.update_pending') : t('vod.manage.editor_tab.publishing')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default EditorHeader; 